
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutGrid, ChevronDown, ChevronUp, Monitor, ArrowRightLeft, 
  ArrowLeft, Settings, Globe, CreditCard, Check, Box, Info, X, Shuffle, Zap
} from './Icons';
import { BrandLogo } from './BrandLogo';
import { topologyData } from '../data/topology';
import { ChannelType } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSettingsMode = location.pathname.startsWith('/settings');
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);
  const priorityRef = useRef<HTMLDivElement>(null);

  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    'Debtors': location.pathname.startsWith('/debtors'),
    'Creditors': location.pathname.startsWith('/creditors'),
    'Channels': location.pathname.includes('/channels')
  });

  const [channelOrder, setChannelOrder] = useState([
    { name: 'eBill', type: ChannelType.EBILL },
    { name: 'EDI', type: ChannelType.EDI },
    { name: 'eMail', type: ChannelType.EMAIL },
    { name: 'Print', type: ChannelType.PRINT },
    { name: 'ePost', type: ChannelType.EPOST },
  ]);

  const moveChannel = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...channelOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    const temp = newOrder[index];
    newOrder[index] = newOrder[targetIndex];
    newOrder[targetIndex] = temp;
    setChannelOrder(newOrder);
  };

  useEffect(() => {
    if (location.pathname.startsWith('/debtors')) setExpandedMenus(p => ({...p, Debtors: true}));
    if (location.pathname.startsWith('/creditors')) setExpandedMenus(p => ({...p, Creditors: true}));
    if (location.pathname.includes('/channels')) setExpandedMenus(p => ({...p, Channels: true}));
  }, [location.pathname]);

  const toggleSubmenu = (name: string) => {
    if (collapsed) {
      setCollapsed(false);
      setExpandedMenus(prev => ({ ...prev, [name]: true }));
    } else {
      setExpandedMenus(prev => ({ ...prev, [name]: !prev[name] }));
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (segment: string) => location.pathname.includes(segment.toLowerCase());

  const getChannelStatusIcon = (type: string) => {
    const status = topologyData.connectivity.find(c => c.type === type);
    if (!status || type === ChannelType.EPOST) {
      return <Settings size={14} className="text-brand-medium dark:text-brand-bright opacity-60 group-hover:opacity-100" />;
    }
    return (
      <Check 
        size={14} 
        strokeWidth={4} 
        className={status.isActive ? "text-brand-medium" : "text-gray-400 dark:text-gray-600"} 
      />
    );
  };

  return (
    <>
      <div className={`flex flex-col h-screen bg-white dark:bg-brand-surface-dark border-r border-gray-200 dark:border-white/5 text-brand-dark transition-all duration-300 ease-in-out ${collapsed ? 'w-20' : 'w-72'} flex-shrink-0 z-20 overflow-hidden shadow-sm`}>
        <div className={`h-16 flex items-center border-b border-brand-light dark:border-white/5 transition-all duration-300 ${collapsed ? 'px-0 justify-center' : 'px-8 space-x-4'}`}>
          <BrandLogo type="main" size={32} />
          {!collapsed && (
            <div className="flex flex-col leading-none animate-in fade-in duration-500">
              <span className="text-xl font-extrabold text-brand-black dark:text-brand-white tracking-tight whitespace-nowrap">
                Octo<span className="text-brand-medium">Suite</span>
              </span>
              <span className="text-[10px] font-bold text-brand-medium uppercase tracking-[0.2em] mt-0.5">Enterprise</span>
            </div>
          )}
        </div>

        <nav className="flex-1 py-6 space-y-1.5 px-3 overflow-y-auto custom-scrollbar overflow-x-hidden relative">
          
          {isSettingsMode && (
            <button
              onClick={() => navigate('/dashboard')}
              className={`w-full flex items-center mb-4 text-brand-medium hover:text-brand-dark dark:text-brand-bright dark:hover:text-white px-5 py-3 rounded-[10px] hover:bg-brand-light/60 dark:hover:bg-white/5 transition-all animate-menu-stagger ${collapsed ? 'justify-center' : ''}`}
            >
              <ArrowLeft size={18} className={collapsed ? '' : 'mr-4'} strokeWidth={3} />
              {!collapsed && <span className="text-sm font-black uppercase tracking-widest">{t('settings')}</span>}
            </button>
          )}

          {!isSettingsMode ? (
            <>
              <button
                onClick={() => navigate('/plan')}
                className={`w-full flex items-center rounded-[10px] transition-all py-3 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} ${
                  isActive('/plan') ? 'bg-brand-light dark:bg-brand-medium/20 text-brand-dark dark:text-brand-white font-bold' : 'text-brand-dark dark:text-gray-300 hover:bg-brand-light/60 dark:hover:bg-white/5'
                }`}
              >
                <div className="flex items-center">
                  <CreditCard size={20} className="text-brand-medium mr-4" strokeWidth={2.5} />
                  {!collapsed && <span className="text-sm font-bold whitespace-nowrap">{t('plan_billing')}</span>}
                </div>
              </button>
              <button
                onClick={() => navigate('/llm')}
                className={`w-full flex items-center rounded-[10px] transition-all py-3 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} ${
                  isActive('/llm') ? 'bg-brand-light dark:bg-brand-medium/20 text-brand-dark dark:text-white font-bold' : 'text-brand-dark dark:text-gray-300 hover:bg-brand-light/60 dark:hover:bg-white/5'
                }`}
              >
                <div className="flex items-center">
                  <Zap size={20} className="text-brand-medium mr-4" strokeWidth={2.5} />
                  {!collapsed && <span className="text-sm font-bold whitespace-nowrap">LLM Assistant</span>}
                </div>
              </button>
              {/* {[
                { label: t('debtors'), icon: Monitor, key: 'Debtors' },
                { label: t('creditors'), icon: ArrowRightLeft, key: 'Creditors' }
              ].map((item) => (
                <div key={item.key} className="animate-menu-stagger">
                  <button
                    onClick={() => toggleSubmenu(item.key)}
                    className={`w-full flex items-center rounded-[10px] transition-all py-3 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} ${
                      isParentActive(item.key) ? 'bg-white dark:bg-brand-medium/20 text-brand-dark dark:text-white font-bold' : 'text-brand-dark dark:text-gray-300 hover:bg-brand-light/60 dark:hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center">
                      <item.icon size={20} className="text-brand-medium mr-4" strokeWidth={2.5} />
                      {!collapsed && <span className="text-sm font-bold whitespace-nowrap">{item.label}</span>}
                    </div>
                    {!collapsed && (expandedMenus[item.key] ? <ChevronUp size={16} className="text-brand-dark dark:text-gray-400 opacity-40" /> : <ChevronDown size={16} className="text-brand-dark dark:text-gray-400 opacity-40" />)}
                  </button>
                  {!collapsed && expandedMenus[item.key] && (
                    <div className="ml-12 border-l border-brand-light dark:border-brand-bright/10 pl-4 py-1 space-y-0.5 mt-1 animate-in slide-in-from-top-2 duration-300">
                      {[
                        { name: t('invoice'), path: 'invoice' },
                        { name: t('orders'), path: 'orders' },
                        { name: t('delivery_notes'), path: 'delivery-notes' },
                        { name: t('credit_notes'), path: 'credit-notes' }
                      ].map(sub => (
                        <div 
                          key={sub.path}
                          onClick={() => navigate(`/${item.key.toLowerCase()}/${sub.path}`)}
                          className={`text-sm font-bold py-2 px-3 rounded-[8px] cursor-pointer transition-colors whitespace-nowrap ${
                            isActive(`/${item.key.toLowerCase()}/${sub.path}`) ? 'text-brand-dark bg-brand-light dark:text-brand-white dark:bg-brand-medium/30' : 'text-brand-dark/70 dark:text-gray-400 hover:text-brand-dark dark:hover:text-white hover:bg-brand-light/40 dark:hover:bg-white/5'
                          }`}
                        >
                          {sub.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))} */}
            </>
          ) : (
            <div className="space-y-1">
              <button
                onClick={() => navigate('/settings/general')}
                className={`w-full flex items-center rounded-[10px] transition-all py-3 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} ${
                  isActive('/settings/general') ? 'bg-brand-light dark:bg-brand-medium/20 text-brand-dark dark:text-white font-bold' : 'text-brand-dark dark:text-gray-300 hover:bg-brand-light/60 dark:hover:bg-white/5'
                }`}
              >
                <div className="flex items-center">
                  <Settings size={20} className="text-brand-medium mr-4" strokeWidth={2.5} />
                  {!collapsed && <span className="text-sm font-bold whitespace-nowrap">{t('general_settings')}</span>}
                </div>
              </button>

              <div className="animate-menu-stagger">
                <button
                  onClick={() => toggleSubmenu('Channels')}
                  className={`w-full flex items-center rounded-[10px] transition-all py-3 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} ${
                    location.pathname.includes('/channels') ? 'bg-brand-light dark:bg-brand-medium/20 text-brand-dark dark:text-white font-bold' : 'text-brand-dark dark:text-gray-300 hover:bg-brand-light/60 dark:hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center">
                    <Globe size={20} className="text-brand-medium mr-4" strokeWidth={2.5} />
                    {!collapsed && <span className="text-sm font-bold whitespace-nowrap">{t('channel_settings')}</span>}
                  </div>
                  {!collapsed && (expandedMenus['Channels'] ? <ChevronUp size={16} className="text-brand-dark dark:text-gray-400 opacity-40" /> : <ChevronDown size={16} className="text-brand-dark dark:text-gray-400 opacity-40" />)}
                </button>
                
                {!collapsed && expandedMenus['Channels'] && (
                  <div className="ml-12 border-l border-brand-light dark:border-brand-bright/10 pl-4 py-1 space-y-0.5 mt-1 animate-in slide-in-from-top-2 duration-300">
                    {channelOrder.map((chan) => (
                      <div 
                        key={chan.name}
                        onClick={() => navigate(`/settings/channels/${chan.name.toLowerCase()}`)}
                        className={`group flex items-center justify-between text-[13px] font-bold py-2 px-3 rounded-[8px] cursor-pointer transition-colors whitespace-nowrap ${
                          isActive(`/settings/channels/${chan.name.toLowerCase()}`) ? 'text-brand-dark bg-brand-light dark:text-brand-white dark:bg-brand-medium/30' : 'text-brand-dark/70 dark:text-gray-400 hover:text-brand-dark dark:hover:text-white hover:bg-brand-light/40 dark:hover:bg-white/5'
                        }`}
                      >
                        <span>{chan.name}</span>
                        {getChannelStatusIcon(chan.type)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => navigate('/settings/plans')}
                className={`w-full flex items-center rounded-[10px] transition-all py-3 ${collapsed ? 'justify-center px-0' : 'justify-between px-5'} ${
                  isActive('/settings/plans') ? 'bg-brand-light dark:bg-brand-medium/20 text-brand-dark dark:text-white font-bold' : 'text-brand-dark dark:text-gray-300 hover:bg-brand-light/60 dark:hover:bg-white/5'
                }`}
              >
                <div className="flex items-center">
                  <CreditCard size={20} className="text-brand-medium mr-4" strokeWidth={2.5} />
                  {!collapsed && <span className="text-sm font-bold whitespace-nowrap">{t('plan_billing')}</span>}
                </div>
              </button>
            </div>
          )}
        </nav>

        <div className={`p-4 border-t border-gray-200 dark:border-white/5 bg-white dark:bg-brand-surface-dark flex justify-center`}>
          <span className="text-[10px] text-brand-dark dark:text-gray-500 font-bold uppercase tracking-widest whitespace-nowrap">
            {collapsed ? 'v4' : '© 2025 OctoSuite v4.0.0'}
          </span>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
